import axios from 'axios'
import {
  checkMSAuthTokenExpired,
  fetchSSOToken,
  getMSAuthTokenFromCookie,
  setTokenToCookie,
} from '../utils/cookies'

// In-memory cache of a currently valid token (non-source of truth, cookie is authoritative)
let currentToken: string | null = null
// Single-flight refresh promise (null when idle)
let refreshPromise: Promise<string> | null = null

const isTokenValid = (token: string) => checkMSAuthTokenExpired(token).isOk()

const startRefresh = (): Promise<string> => {
  if (!refreshPromise) {
    refreshPromise = fetchSSOToken()
      .andThen(setTokenToCookie)
      .match(
        (token) => {
          currentToken = token
          return token
        },
        (err) => {
          throw err
        }
      )
      .finally(() => {
        // Allow subsequent refresh attempts
        refreshPromise = null
      }) as unknown as Promise<string>
  }
  return refreshPromise
}

const getOrRefreshToken = async (): Promise<string | null> => {
  // Fast path: in-memory token still valid
  if (currentToken && isTokenValid(currentToken)) return currentToken

  // Check cookie
  const cookieTokenResult = getMSAuthTokenFromCookie().andThen(
    checkMSAuthTokenExpired
  )
  if (cookieTokenResult.isOk()) {
    currentToken = cookieTokenResult.value
    return currentToken
  }

  // Need refresh
  try {
    const token = await startRefresh()
    return token
  } catch (e) {
    return null
  }
}

export const apiClient = axios.create({
  baseURL: `${process.env.REACT_APP_API_ENDPOINT}/app`,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

apiClient.interceptors.request.use(async (config) => {
  const token = await getOrRefreshToken()
  if (token) {
    config.headers = config.headers || {}
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// 401 handler: attempt one refresh + replay
apiClient.interceptors.response.use(
  (res) => res,
  async (error) => {
    const status = error?.response?.status
    const originalRequest = error?.config
    if (status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true
      try {
        const token = await startRefresh()
        if (token) {
          originalRequest.headers = originalRequest.headers || {}
          originalRequest.headers.Authorization = `Bearer ${token}`
          return apiClient.request(originalRequest)
        }
      } catch (_) {
        // fall through
      }
    }
    return Promise.reject(error)
  }
)

export const externalClient = axios.create({
  timeout: 600000, // 10 minutes in milliseconds
})
